import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  MapPin,
  User,
  Filter,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlertsPage = () => {
  const { alerts, setAlerts, isSecurityHead, hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [clearing, setClearing] = useState(false);

  // Permission checks
  const canResolve = hasPermission('can_resolve_alerts');
  const canDelete = hasPermission('can_delete_alerts');

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/alerts`);
      setAlerts(response.data);
    } catch (error) {
      toast.error('Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/alerts/${alertId}`, {
        status: 'resolved',
        resolution_notes: 'Resolved from control panel'
      });
      toast.success('Alert resolved');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const deleteAlert = async (alertId) => {
    if (!window.confirm('Delete this alert permanently?')) return;
    try {
      await axios.delete(`${API_URL}/alerts/${alertId}`);
      toast.success('Alert deleted');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const clearResolvedAlerts = async () => {
    const resolvedAlerts = alerts.filter(a => a.status === 'resolved');
    if (resolvedAlerts.length === 0) {
      toast.info('No resolved alerts to clear');
      return;
    }
    
    if (!window.confirm(`Delete ${resolvedAlerts.length} resolved alert(s)? This cannot be undone.`)) {
      return;
    }
    
    setClearing(true);
    try {
      await Promise.all(resolvedAlerts.map(a => axios.delete(`${API_URL}/alerts/${a.id}`)));
      toast.success(`Cleared ${resolvedAlerts.length} resolved alerts`);
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to clear some alerts');
      fetchAlerts();
    } finally {
      setClearing(false);
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    return alert.status === filter;
  });

  const getCategoryColor = (category) => {
    const colors = {
      threat: '#ef4444',
      medical: '#0ea5e9',
      disturbance: '#f97316',
      fire: '#eab308',
      child_safety: '#a855f7',
      general: '#a1a1aa'
    };
    return colors[category] || colors.general;
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return {
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };
  };

  return (
    <div className="p-4 md:p-6 space-y-6 pb-24" data-testid="alerts-page">
      {/* Header with Logo */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <PageHeader 
          title="Alert History" 
          subtitle={`${filteredAlerts.length} alerts`}
        />
        <div className="flex gap-2">
          {/* Refresh Button */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchAlerts}
            disabled={loading}
            className="border-white/20 hover:bg-white/5 rounded-sm font-mono text-xs uppercase"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {/* Clear Resolved - Admin only */}
          {canDelete && alerts.some(a => a.status === 'resolved') && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={clearResolvedAlerts}
              disabled={clearing}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-sm font-mono text-xs uppercase"
            >
              <Trash2 className={`w-4 h-4 mr-2 ${clearing ? 'animate-pulse' : ''}`} />
              {clearing ? 'Clearing...' : 'Clear Resolved'}
            </Button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <Tabs defaultValue="all" onValueChange={setFilter}>
        <TabsList className="bg-panel border border-white/10 rounded-sm p-1">
          <TabsTrigger 
            value="all" 
            className="font-mono text-xs uppercase rounded-sm data-[state=active]:bg-white data-[state=active]:text-black"
            data-testid="filter-all"
          >
            All
          </TabsTrigger>
          <TabsTrigger 
            value="active" 
            className="font-mono text-xs uppercase rounded-sm data-[state=active]:bg-alert-red data-[state=active]:text-white"
            data-testid="filter-active"
          >
            Active
          </TabsTrigger>
          <TabsTrigger 
            value="resolved" 
            className="font-mono text-xs uppercase rounded-sm data-[state=active]:bg-green-600 data-[state=active]:text-white"
            data-testid="filter-resolved"
          >
            Resolved
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <AlertList 
            alerts={filteredAlerts} 
            onResolve={canResolve ? resolveAlert : null}
            onDelete={canDelete ? deleteAlert : null}
            getCategoryColor={getCategoryColor}
            formatDateTime={formatDateTime}
          />
        </TabsContent>
        <TabsContent value="active" className="mt-4">
          <AlertList 
            alerts={filteredAlerts} 
            onResolve={canResolve ? resolveAlert : null}
            onDelete={canDelete ? deleteAlert : null}
            getCategoryColor={getCategoryColor}
            formatDateTime={formatDateTime}
          />
        </TabsContent>
        <TabsContent value="resolved" className="mt-4">
          <AlertList 
            alerts={filteredAlerts} 
            onResolve={canResolve ? resolveAlert : null}
            onDelete={canDelete ? deleteAlert : null}
            getCategoryColor={getCategoryColor}
            formatDateTime={formatDateTime}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const AlertList = ({ alerts, onResolve, onDelete, getCategoryColor, formatDateTime }) => {
  if (alerts.length === 0) {
    return (
      <Card className="bg-panel border-white/10 rounded-sm">
        <CardContent className="p-12 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4 opacity-50" />
          <p className="font-chivo font-bold text-xl text-white">No Alerts</p>
          <p className="font-inter text-sm text-muted-foreground mt-1">
            No alerts match the current filter
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {alerts.map((alert) => {
        const { time, date } = formatDateTime(alert.created_at);
        const color = getCategoryColor(alert.category);
        
        return (
          <Card 
            key={alert.id} 
            className={`bg-panel border-white/10 rounded-sm card-hover ${
              alert.status === 'active' ? 'border-l-4' : ''
            }`}
            style={{ borderLeftColor: alert.status === 'active' ? color : undefined }}
            data-testid={`alert-card-${alert.id}`}
          >
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Alert Info */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge 
                      variant="outline" 
                      className="font-mono text-[10px] uppercase tracking-wider rounded-sm"
                      style={{ borderColor: color, color: color }}
                    >
                      {alert.label || alert.category}
                    </Badge>
                    <Badge 
                      variant={alert.status === 'active' ? 'destructive' : 'secondary'}
                      className="font-mono text-[10px] uppercase rounded-sm"
                    >
                      {alert.status}
                    </Badge>
                  </div>
                  
                  <p className="font-inter text-sm text-white">
                    {alert.description || 'No description provided'}
                  </p>
                  
                  <div className="flex items-center gap-4 text-muted-foreground flex-wrap">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span className="font-mono text-xs">{alert.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span className="font-mono text-xs">{alert.created_by_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span className="font-mono text-xs">{time} • {date}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 items-center">
                  {alert.status === 'active' && onResolve && (
                    <Button
                      onClick={() => onResolve(alert.id)}
                      className="bg-green-600 hover:bg-green-700 text-white font-mono text-xs uppercase rounded-sm"
                      data-testid={`resolve-btn-${alert.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Resolve
                    </Button>
                  )}
                </div>
              </div>

              {/* Resolution Info */}
              {alert.status === 'resolved' && alert.resolved_at && (
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs text-green-500">
                      Resolved at {formatDateTime(alert.resolved_at).time} • {formatDateTime(alert.resolved_at).date}
                    </p>
                    {alert.resolution_notes && (
                      <p className="font-inter text-xs text-muted-foreground mt-1">
                        {alert.resolution_notes}
                      </p>
                    )}
                  </div>
                  {/* Delete button - only on resolved alerts, admin only */}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(alert.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 font-mono text-[10px] uppercase"
                      title="Delete alert permanently"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AlertsPage;
